import React, { useState, useEffect } from 'react';
import { FiGlobe, FiMonitor, FiSmartphone, FiUsers, FiTrendingUp, FiMapPin, FiWifi, FiRefreshCw } from 'react-icons/fi';

interface VisitorData {
  timestamp: string;
  ip: string;
  country?: string;
  city?: string;
  region?: string;
  isp?: string;
  org?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
  userAgent: string;
  browser?: string;
  os?: string;
  device?: string;
  referrer?: string;
  language?: string;
  screenResolution?: string;
  pathname?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

interface Stats {
  totalVisitors: number;
  last24h: number;
  last7d: number;
  last30d: number;
  topCountries: { name: string; count: number }[];
  topCities: { name: string; count: number }[];
  browsers: { name: string; count: number }[];
  operatingSystems: { name: string; count: number }[];
  devices: { name: string; count: number }[];
  topISPs: { name: string; count: number }[];
}

export default function AnalyticsDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [visitors, setVisitors] = useState<VisitorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'visitors' | 'locations' | 'devices'>('overview');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch('/api/trackVisitor?action=stats');
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch recent visitors
      const visitorsRes = await fetch('/api/trackVisitor?action=visitors&limit=100');
      const visitorsData = await visitorsRes.json();
      setVisitors(visitorsData.visitors || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-cream dark:bg-brand-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-coral mx-auto mb-4"></div>
          <p className="text-brand-charcoal dark:text-white">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-brand-cream dark:bg-brand-black">
        <div className="text-center">
          <p className="text-brand-charcoal dark:text-white">Failed to load analytics</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-brand-coral text-white rounded-lg hover:bg-brand-coral/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream dark:bg-brand-black p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-brand-charcoal dark:text-white mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-brand-charcoal/60 dark:text-white/60">
              Real-time visitor tracking and insights
            </p>
          </div>
          <button
            onClick={fetchAnalytics}
            className="flex items-center gap-2 px-4 py-2 bg-brand-coral text-white rounded-lg hover:bg-brand-coral/90 transition-colors"
          >
            <FiRefreshCw size={18} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<FiUsers size={24} />}
            label="Total Visitors"
            value={stats.totalVisitors.toLocaleString()}
            color="bg-blue-500"
          />
          <StatCard
            icon={<FiTrendingUp size={24} />}
            label="Last 24 Hours"
            value={stats.last24h.toLocaleString()}
            color="bg-green-500"
          />
          <StatCard
            icon={<FiGlobe size={24} />}
            label="Last 7 Days"
            value={stats.last7d.toLocaleString()}
            color="bg-purple-500"
          />
          <StatCard
            icon={<FiMonitor size={24} />}
            label="Last 30 Days"
            value={stats.last30d.toLocaleString()}
            color="bg-orange-500"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-brand-charcoal/10 dark:border-white/10">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            icon={<FiTrendingUp size={18} />}
            label="Overview"
          />
          <TabButton
            active={activeTab === 'visitors'}
            onClick={() => setActiveTab('visitors')}
            icon={<FiUsers size={18} />}
            label="Visitors"
          />
          <TabButton
            active={activeTab === 'locations'}
            onClick={() => setActiveTab('locations')}
            icon={<FiMapPin size={18} />}
            label="Locations"
          />
          <TabButton
            active={activeTab === 'devices'}
            onClick={() => setActiveTab('devices')}
            icon={<FiSmartphone size={18} />}
            label="Devices"
          />
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Top Countries" icon={<FiGlobe size={20} />}>
              {stats.topCountries.map((country, i) => (
                <BarItem
                  key={i}
                  label={country.name}
                  value={country.count}
                  total={stats.totalVisitors}
                  color="bg-blue-500"
                />
              ))}
            </ChartCard>

            <ChartCard title="Top Cities" icon={<FiMapPin size={20} />}>
              {stats.topCities.map((city, i) => (
                <BarItem
                  key={i}
                  label={city.name}
                  value={city.count}
                  total={stats.totalVisitors}
                  color="bg-green-500"
                />
              ))}
            </ChartCard>

            <ChartCard title="Browsers" icon={<FiMonitor size={20} />}>
              {stats.browsers.map((browser, i) => (
                <BarItem
                  key={i}
                  label={browser.name}
                  value={browser.count}
                  total={stats.totalVisitors}
                  color="bg-purple-500"
                />
              ))}
            </ChartCard>

            <ChartCard title="Operating Systems" icon={<FiMonitor size={20} />}>
              {stats.operatingSystems.map((os, i) => (
                <BarItem
                  key={i}
                  label={os.name}
                  value={os.count}
                  total={stats.totalVisitors}
                  color="bg-orange-500"
                />
              ))}
            </ChartCard>
          </div>
        )}

        {activeTab === 'visitors' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-brand-charcoal/5 dark:bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">IP</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">ISP</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">Browser</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-brand-charcoal dark:text-white">Device</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-charcoal/10 dark:divide-white/10">
                  {visitors.map((visitor, i) => (
                    <tr key={i} className="hover:bg-brand-charcoal/5 dark:hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-sm text-brand-charcoal dark:text-white/80">
                        {new Date(visitor.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-brand-charcoal dark:text-white/80">
                        {visitor.ip}
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-charcoal dark:text-white/80">
                        {visitor.city}, {visitor.country}
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-charcoal dark:text-white/80">
                        {visitor.isp}
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-charcoal dark:text-white/80">
                        {visitor.browser} on {visitor.os}
                      </td>
                      <td className="px-4 py-3 text-sm text-brand-charcoal dark:text-white/80">
                        {visitor.device}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'locations' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Top Countries" icon={<FiGlobe size={20} />}>
              {stats.topCountries.map((country, i) => (
                <BarItem
                  key={i}
                  label={country.name}
                  value={country.count}
                  total={stats.totalVisitors}
                  color="bg-blue-500"
                  showPercentage
                />
              ))}
            </ChartCard>

            <ChartCard title="Top Cities" icon={<FiMapPin size={20} />}>
              {stats.topCities.map((city, i) => (
                <BarItem
                  key={i}
                  label={city.name}
                  value={city.count}
                  total={stats.totalVisitors}
                  color="bg-green-500"
                  showPercentage
                />
              ))}
            </ChartCard>

            <ChartCard title="Top ISPs" icon={<FiWifi size={20} />}>
              {stats.topISPs.map((isp, i) => (
                <BarItem
                  key={i}
                  label={isp.name}
                  value={isp.count}
                  total={stats.totalVisitors}
                  color="bg-purple-500"
                  showPercentage
                />
              ))}
            </ChartCard>
          </div>
        )}

        {activeTab === 'devices' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Device Types" icon={<FiSmartphone size={20} />}>
              {stats.devices.map((device, i) => (
                <BarItem
                  key={i}
                  label={device.name}
                  value={device.count}
                  total={stats.totalVisitors}
                  color="bg-blue-500"
                  showPercentage
                />
              ))}
            </ChartCard>

            <ChartCard title="Browsers" icon={<FiMonitor size={20} />}>
              {stats.browsers.map((browser, i) => (
                <BarItem
                  key={i}
                  label={browser.name}
                  value={browser.count}
                  total={stats.totalVisitors}
                  color="bg-green-500"
                  showPercentage
                />
              ))}
            </ChartCard>

            <ChartCard title="Operating Systems" icon={<FiMonitor size={20} />}>
              {stats.operatingSystems.map((os, i) => (
                <BarItem
                  key={i}
                  label={os.name}
                  value={os.count}
                  total={stats.totalVisitors}
                  color="bg-purple-500"
                  showPercentage
                />
              ))}
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
      <div className="flex items-center gap-4">
        <div className={`${color} text-white p-3 rounded-lg`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-brand-charcoal/60 dark:text-white/60">{label}</p>
          <p className="text-2xl font-bold text-brand-charcoal dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 ${
        active
          ? 'text-brand-coral border-brand-coral'
          : 'text-brand-charcoal/60 dark:text-white/60 border-transparent hover:text-brand-charcoal dark:hover:text-white'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function ChartCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-brand-coral">{icon}</div>
        <h3 className="text-lg font-semibold text-brand-charcoal dark:text-white">{title}</h3>
      </div>
      <div className="space-y-3">
        {children}
      </div>
    </div>
  );
}

function BarItem({ label, value, total, color, showPercentage }: { label: string; value: number; total: number; color: string; showPercentage?: boolean }) {
  const percentage = (value / total) * 100;
  
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-brand-charcoal dark:text-white/80">{label}</span>
        <span className="text-sm font-semibold text-brand-charcoal dark:text-white">
          {value.toLocaleString()}
          {showPercentage && ` (${percentage.toFixed(1)}%)`}
        </span>
      </div>
      <div className="w-full bg-brand-charcoal/10 dark:bg-white/10 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
